import { ref } from 'vue';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

// Singleton instance to avoid re-initializing
let fpPromise = null;
let cachedFingerprint = null;

export function useFingerprint() {
    const fingerprint = ref(cachedFingerprint);
    const loading = ref(!cachedFingerprint);
    const error = ref(null);

    /**
     * Initialize FingerprintJS and get the visitor ID.
     * The fingerprint is cached for the session.
     */
    async function initialize() {
        // Return cached fingerprint if available
        if (cachedFingerprint) {
            fingerprint.value = cachedFingerprint;
            loading.value = false;
            return cachedFingerprint;
        }

        try {
            loading.value = true;
            error.value = null;

            // Create FingerprintJS instance (reuse if already created)
            if (!fpPromise) {
                fpPromise = FingerprintJS.load();
            }

            const fp = await fpPromise;
            const result = await fp.get();

            // Cache the fingerprint
            cachedFingerprint = result.visitorId;
            fingerprint.value = cachedFingerprint;

            // Also store in localStorage as fallback
            localStorage.setItem('device_fingerprint', cachedFingerprint);

            return cachedFingerprint;
        } catch (err) {
            console.error('Failed to get fingerprint:', err);
            error.value = err;

            // Fallback to stored fingerprint or generate a random one
            const stored = localStorage.getItem('device_fingerprint');
            if (stored) {
                cachedFingerprint = stored;
                fingerprint.value = stored;
                return stored;
            }

            // Last resort: generate a pseudo-random ID
            const fallbackId = 'fallback_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
            localStorage.setItem('device_fingerprint', fallbackId);
            cachedFingerprint = fallbackId;
            fingerprint.value = fallbackId;
            return fallbackId;
        } finally {
            loading.value = false;
        }
    }

    /**
     * Get the fingerprint, initializing if necessary.
     */
    async function getFingerprint() {
        if (cachedFingerprint) {
            return cachedFingerprint;
        }
        return await initialize();
    }

    /**
     * Check if fingerprint is ready.
     */
    function isReady() {
        return !!fingerprint.value;
    }

    return {
        fingerprint,
        loading,
        error,
        initialize,
        getFingerprint,
        isReady
    };
}
